import { cva } from "class-variance-authority"

// Literal per-variant class maps, ported verbatim from the pre-CVA `card()`
// helper's resolved output. See button.js's header comment for why nothing
// here is built from a template: whole literal class lists (like LIFT) may be
// interpolated, class fragments may not.
//
// A sortable item's lift while `data-dragging="true"`: it floats above its
// neighbours on a shadow and grows slightly through `scale`. A sortable
// item's inline `transform` belongs to dnd-kit, so its transition never lists
// `transform` — animating it makes the item trail the pointer while dragging
// and fall from above on drop.
const LIFT =
  "data-[dragging=true]:z-30 data-[dragging=true]:shadow-xl data-[dragging=true]:[scale:1.02]"

export const cardRecipe = cva("", {
  variants: {
    variant: {
      // The top-level surface of a route or a dialog — see
      // `.claude/rules/design/layout.md` for the below-`sm` "shell is the
      // surface" rule this token encodes.
      shell:
        "bg-white border-y border-neutral-200 transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-300 ease-out p-4 sm:mx-4 sm:border sm:rounded-xl sm:p-6 md:rounded-2xl lg:p-8",
      interactive:
        "bg-white border border-neutral-200 rounded-xl md:rounded-2xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-300 ease-out group block active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer hover:-translate-y-0.5 hover:border-neutral-300",
      section:
        "bg-white border border-neutral-200 rounded-xl md:rounded-2xl transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-300 ease-out p-4 sm:p-6 md:p-8",
      nested: "p-3 sm:p-4 border border-neutral-200 rounded-lg bg-neutral-50",
      // Sortable surfaces (`row`, `thumb`) lift through LIFT and never
      // transition `transform` — see LIFT above.
      row: `bg-white border border-neutral-200 rounded-xl md:rounded-2xl transition-[color,background-color,border-color,box-shadow,opacity,scale] duration-300 ease-out p-3 sm:p-4 data-[dragging=true]:border-transparent ${LIFT}`,
      // One image in a sortable thumbnail strip; the image itself supplies
      // the look, so the lift is its only surface treatment.
      thumb: `relative rounded-lg transition-[box-shadow,scale] duration-300 ease-out ${LIFT}`,
      // Surfaces' own tint ramp (spec §1.2: "page and card backgrounds") —
      // not Interactive's `primary` family, so this box's hue/strength is
      // independent of the button colour and dials with Surfaces' own
      // strength/depth controls. The highlight text living inside it
      // (`tokens.text.highlightLabel`/`highlightValue`/`highlightCaption`)
      // is Content's family instead — the two can differ by design once a
      // merchant sets Content/Surfaces to different sources.
      highlight: "p-4 sm:p-6 bg-tint-50 rounded-lg border border-tint-200",
      highlightMuted: "p-4 sm:p-6 bg-neutral-100 rounded-lg shadow-inner",
      // WhatsApp's own brand green (never tenant-themed, like
      // `buttonRecipe`'s `whatsapp`) on a success-ramp ground: marks a
      // setting that the "Order on WhatsApp" path depends on.
      whatsapp: "p-3 sm:p-4 bg-green-50 rounded-lg border-2 border-[#25D366]",
      // Selection is carried by `data-state` (`checked`/`unchecked`) set by
      // the caller from its own selected-value check — never a JS ternary
      // choosing between two whole class strings (radix-migration N12: the
      // old PaymentOptionSelector used color alone via a ternary, which also
      // fails colour-independence; pair this with a real checkmark/dot
      // indicator on the control itself, not border/bg color alone).
      selectable:
        "p-4 rounded-lg border-2 cursor-pointer transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 ease-out hover:shadow-md data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-50 data-[state=checked]:shadow-md data-[state=unchecked]:border-neutral-300 data-[state=unchecked]:bg-white data-[state=unchecked]:hover:border-neutral-400",
    },
  },
  defaultVariants: { variant: "section" },
})
